// Mock data for the redesigned GD landing page (v2). Design-phase only: these
// arrays stand in for the catalog/settings services and will be swapped for real
// API data during integration. Images reuse existing repo assets where possible.

const ASSET = "/iceholidays-assets/images";

// A nav entry. The navbar is deliberately kept to a single level of dropdown —
// no submenus — so every child is a leaf. A child with no `href` renders as
// non-clickable text, which is how not-yet-launched destinations (Hotels) show.
export interface NavChild {
  label: string;
  href?: string;
}

export interface NavItem {
  label: string;
  children?: NavChild[];
}

export const NAV_ITEMS: NavItem[] = [
  { label: "Tours", children: [
    { label: "GD Classic", href: "/web/listing?brands=GD_Standard&type=series" },
    { label: "GD In-Depth", href: "/web/listing?brands=GD_In_Depth&type=series" },
  ] },
  { label: "Cruises", children: [
    { label: "GD World Cruises", href: "/web/posts/world" },
    { label: "GD FlyCruise", href: "/web/posts/fly" },
  ] },
  { label: "Flights", children: [{ label: "GD Flight", href: "/web/gd-flight-about-us" }] },
  { label: "Hotels", children: [{ label: "Book a Hotel (Coming Soon)" }] },
  // /web/posts/trip serves the G-Trip catalogue, which is the same product as
  // the "Ground" nav label.
  { label: "Ground", children: [{ label: "Ground", href: "/web/posts/trip" }] },
  { label: "Corporate", children: [
    { label: "MICE", href: "/web/corporate/mice" },
    { label: "Business Travel", href: "/web/corporate/business-travel" },
    { label: "Travel Warrant", href: "/web/corporate/gd-warrant" },
  ] },
  { label: "Others", children: [
    { label: "About Us", href: "/web/about-us" },
    { label: "Contact Us", href: "/web/contact-us" },
  ] },
];

// Exclusive Deals is now sourced from ExclusiveDealsApi (brand_exclusive_deals);
// kept here unused as a reference for the design-phase shape/copy.
export interface MockDeal {
  id: number;
  headline: string;   // left panel, e.g. "Up to RM 1,000 Off"
  title: string;      // right panel title, e.g. "Buy 1 Free 1"
  subtitle: string;   // e.g. "Tours & Cruises, no min spend."
  cta: string;
}

export const MOCK_DEALS: MockDeal[] = [
  { id: 1, headline: "Up to RM 1,000 Off", title: "Buy 1 Free 1", subtitle: "Tours & Cruises, no min spend.", cta: "View Product" },
  { id: 2, headline: "50% Off", title: "Cruise Companion Deal", subtitle: "Selected Cruises Only", cta: "View Product" },
  { id: 3, headline: "Up to RM 1,000 Off", title: "Early Booking Deal", subtitle: "Tours & Cruises, no min spend.", cta: "View Product" },
  { id: 4, headline: "50% Off", title: "Cruise Companion Deal", subtitle: "Selected Cruises Only", cta: "View Product" },
  { id: 5, headline: "Up to RM 800 Off", title: "Buy 1 Free 1", subtitle: "Tours & Cruises, no min spend.", cta: "View Product" },
];

export interface Offer {
  id: number;
  image: string;
  badge: string;
  brandLogo: string;
  location: string;
  from: string;
  title: string;
  guaranteed: boolean;
  originalPrice: string;
  save: string;
  price: string;
}

export const OFFERS: Offer[] = [
  { id: 1, image: `${ASSET}/china_listings_cover.png`, badge: "Buy 1 Free 1", brandLogo: `${ASSET}/gd-logo_round.png`, location: "Langkawi", from: "Jakarta", title: "Bali Cultural Experience & Ubud Tour 4D3N", guaranteed: true, originalPrice: "RM 10,689", save: "RM 4,000", price: "RM 9,689" },
  { id: 2, image: `${ASSET}/china_listings_cover.png`, badge: "Buy 1 Free 1", brandLogo: `${ASSET}/gd-logo_round.png`, location: "Tokyo", from: "Osaka", title: "Tokyo City Explorer & Mt. Fuji Trip 3D2N", guaranteed: true, originalPrice: "RM 10,689", save: "RM 4,000", price: "RM 9,689" },
  { id: 3, image: `${ASSET}/gdcruise_cover.png`, badge: "Buy Second 50% Off", brandLogo: `${ASSET}/gd-logo_round.png`, location: "Sydney", from: "Melbourne", title: "Sydney Harbour Cruise & Blue Mountains 4D3N", guaranteed: true, originalPrice: "RM 10,689", save: "RM 4,000", price: "RM 9,689" },
  { id: 4, image: `${ASSET}/sabah_cover.png`, badge: "Buy Second 50% Off", brandLogo: `${ASSET}/gd_world-cruises_logo.png`, location: "New York", from: "Melbourne", title: "New York City Highlights & Broadway Show 3D2N", guaranteed: true, originalPrice: "RM 10,689", save: "RM 4,000", price: "RM 9,689" },
  { id: 5, image: `${ASSET}/china_listings_cover.png`, badge: "Buy 1 Free 1", brandLogo: `${ASSET}/gd-logo_round.png`, location: "Budapest", from: "Melbourne", title: "Budapest Danube River Tour 3D2N", guaranteed: true, originalPrice: "RM 10,689", save: "RM 4,000", price: "RM 9,689" },
];

export interface PromoBanner {
  id: number;
  image: string;
}

export const PROMO_BANNERS: PromoBanner[] = [
  { id: 1, image: `${ASSET}/limited_time_promo_1.png` },
  { id: 2, image: `${ASSET}/limited_time_promo_2.png` },
  { id: 3, image: `${ASSET}/limited_time_promo_3.png` },
  { id: 4, image: `${ASSET}/limited_time_promo_4.png` },
];

export interface Destination {
  id: number;
  name: string;
  image: string;
}

export const DESTINATIONS: Destination[] = [
  { id: 1, name: "Japan", image: `${ASSET}/destinations/japan.png` },
  { id: 2, name: "China", image: `${ASSET}/destinations/china.png` },
  { id: 3, name: "Korea", image: `${ASSET}/destinations/korea.png` },
  { id: 4, name: "Switzerland", image: `${ASSET}/destinations/switzerland.png` },
  { id: 5, name: "Europe", image: `${ASSET}/destinations/europe.png` },
  { id: 6, name: "United Kingdom", image: `${ASSET}/destinations/uk.png` },
  { id: 7, name: "Canada", image: `${ASSET}/destinations/canada.png` },
  { id: 8, name: "Indonesia", image: `${ASSET}/destinations/indonesia.png` },
  { id: 9, name: "Brazil", image: `${ASSET}/destinations/brazil.png` },
];

export interface Collection {
  id: number;
  name: string;
  logo: string;
  href: string;
}

export const COLLECTIONS: Collection[] = [
  { id: 1, name: "GD Classic", logo: `${ASSET}/gd-logo_round.png`, href: "/web/series-tour/gd-classics" },
  { id: 2, name: "The Signature Tour", logo: `${ASSET}/TS_Logo.png`, href: "/web" },
  { id: 3, name: "GD In-Depth", logo: `${ASSET}/gd-logo_round.png`, href: "/web/series-tour/gd-indepth" },
  { id: 4, name: "FS Holiday", logo: `${ASSET}/icons/fs.png`, href: "/web" },
  { id: 5, name: "GD World Cruises", logo: `${ASSET}/gd_world-cruises_logo.png`, href: "/web" },
  { id: 6, name: "GD FlyCruise", logo: `${ASSET}/gd_fly-cruises_logo.png`, href: "/web" },
  { id: 7, name: "GD Flight", logo: `${ASSET}/gd_flight_icon.png`, href: "/web/gd-flight-about-us" },
  { id: 8, name: "G-trip", logo: `${ASSET}/gtrip_logo.png`, href: "/web" },
];

export const FOOTER = {
  // Brands with an href render as links; external sites (http…) open in a new
  // tab, internal /web paths use client-side routing. Brands without an href
  // render as plain text until their destination is confirmed.
  brands: [
    { label: "GD Classic", href: "/web/listing?brands=GD_Standard&type=series" },
    { label: "The Signature Tours", href: "https://www.thesignaturetours.com/web" },
    { label: "GD In-Depth", href: "/web/listing?brands=GD_In_Depth&type=series" },
    { label: "FS Holidays", href: "https://fsholidays.my/" },
    { label: "GD World Cruises", href: "/web/listing?brands=CRUISE&type=cruise" },
    { label: "GD FlyCruise", href: "/web/listing?brands=GD_FLY_CRUISE&type=cruise" },
  ] as { label: string; href?: string }[],
  awards: [
    `${ASSET}/footer/award-soba.png`,
    `${ASSET}/footer/award-sinchew.png`,
    `${ASSET}/footer/award-putra-aria.png`,
    `${ASSET}/footer/award-brandlaureate.png`,
    `${ASSET}/footer/award-msia-china.png`,
  ],
  company: [
    { label: "About Us", href: "/web/about-us" },
    { label: "PDPA", href: "/web/footer/pdpa" },
    { label: "News and Events", href: "/web/blogs?type=news_and_events" },
    { label: "Terms & Conditions", href: "/web/footer/terms_and_condition" },
  ],
  support: [
    { label: "Contact Us", href: "/web/contact-us" },
    { label: "Contact Agents", href: "/web/contact-agents" },
  ],
  payments: [`${ASSET}/payment/master_v2.png`, `${ASSET}/payment/fpx-new.png`, `${ASSET}/payment/visa.png`],
  // Follow Us icons exported from Figma (frame 47) — full-colour brand marks.
  // Links point to GD's official social accounts (same as the legacy footer).
  socials: [
    { icon: `${ASSET}/social/fb_v2.png`, href: "https://www.facebook.com/gdgoldendestinations" },
    { icon: `${ASSET}/social/yt_v2.png`, href: "https://www.youtube.com/@GoldenDestinationsGD" },
    { icon: `${ASSET}/social/ig_v2.png`, href: "https://www.instagram.com/gdgoldendestinations/?hl=en" },
    { icon: `${ASSET}/social/xhs_v2.png`, href: "https://www.xiaohongshu.com/user/profile/664aaf560000000007007b09" },
  ],
};
